// Utility functions for text and description handling

/**
 * Check if description text is too long
 * @param {string} text - The description text to check
 * @param {number} maxLength - Maximum allowed length (default: 150 characters)
 * @returns {boolean} - True if text is too long
 */
export const isDescriptionTooLong = (text, maxLength = 150) => {
  if (!text) return false;
  
  // Remove HTML tags for accurate character count
  const plainText = text.replace(/<[^>]*>/g, '');
  return plainText.length > maxLength;
};

/**
 * Truncate description text to specified length
 * @param {string} text - The description text to truncate
 * @param {number} maxLength - Maximum allowed length (default: 150 characters)
 * @param {string} suffix - Suffix to add when truncated (default: '...')
 * @returns {string} - Truncated text
 */
export const truncateDescription = (text, maxLength = 150, suffix = '...') => {
  if (!text) return '';
  
  // Remove HTML tags for accurate character count
  const plainText = text.replace(/<[^>]*>/g, '');
  
  if (plainText.length <= maxLength) {
    return text;
  }
  
  // Truncate plain text
  const truncatedPlainText = plainText.substring(0, maxLength - suffix.length) + suffix;
  
  // Try to preserve some basic HTML formatting
  const hasHtml = /<[^>]*>/.test(text);
  if (!hasHtml) {
    return truncatedPlainText;
  }
  
  // For HTML content, we'll return the truncated plain text
  // You can enhance this to preserve HTML structure if needed
  return truncatedPlainText;
};

/**
 * Get the number of lines a text would occupy
 * @param {string} text - The text to analyze
 * @param {number} lineHeight - Line height in pixels (default: 1.4em)
 * @param {number} containerWidth - Container width in pixels
 * @returns {number} - Estimated number of lines
 */
export const getEstimatedLines = (text, lineHeight = 1.4, containerWidth = 300) => {
  if (!text) return 0;
  
  const plainText = text.replace(/<[^>]*>/g, '');
  const avgCharWidth = 8; // Approximate character width in pixels
  const charsPerLine = Math.floor(containerWidth / avgCharWidth);
  
  if (charsPerLine <= 0) return 1;
  
  const lines = Math.ceil(plainText.length / charsPerLine);
  return lines;
};

/**
 * Check if text would overflow based on line count
 * @param {string} text - The text to check
 * @param {number} maxLines - Maximum allowed lines (default: 3)
 * @param {number} containerWidth - Container width in pixels (default: 300)
 * @returns {boolean} - True if text would overflow
 */
export const wouldTextOverflow = (text, maxLines = 3, containerWidth = 300) => {
  const estimatedLines = getEstimatedLines(text, 1.4, containerWidth);
  return estimatedLines > maxLines;
};

/**
 * Get CSS class based on description length
 * @param {string} description - The description text
 * @param {number} maxLength - Maximum allowed length (default: 150)
 * @returns {string} - CSS class name
 */
export const getDescriptionClass = (description, maxLength = 150) => {
  if (!description) return 'max-lines';
  
  if (isDescriptionTooLong(description, maxLength)) {
    return 'max-lines long-description';
  }
  
  return 'max-lines';
};

/**
 * Get inline styles based on description length
 * @param {string} description - The description text
 * @param {number} maxLength - Maximum allowed length (default: 150)
 * @returns {object} - Inline styles object
 */
export const getDescriptionStyles = (description, maxLength = 150) => {
  const baseStyles = { minHeight: "2.8em" };
  
  if (!description) return baseStyles;
  
  if (isDescriptionTooLong(description, maxLength)) {
    return {
      ...baseStyles,
      position: 'relative',
      cursor: 'pointer'
    };
  }
  
  return baseStyles;
};

/**
 * Get responsive price text based on screen width
 * @param {string} priceText - The original price text
 * @param {string} type - 'standard' or 'membership'
 * @returns {string} - Responsive price text
 */
export const getResponsivePriceText = (priceText, type = 'standard') => {
  if (!priceText) return '';
  
  // Check if screen width is 375px or less
  const isSmallScreen = window.innerWidth <= 375;
  
  if (!isSmallScreen) {
    return priceText;
  }
  
  // Replace text for small screens
  if (type === 'standard') {
    return priceText.replace('Standard: $', 'Std:');
  } else {
    return priceText.replace('Member: $', 'Mem:');
  }
}; 